import { ActionButtonText } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { SeverityLevelsInfoModal } from './SeverityLevelsInfoModal';

import { handleError } from 'services/Error';
import { useGetSeverityLevelsQuery, useResolveIncidentMutation } from 'store/api/oopsApi';

const ValidationSchema = Yup.object({
    resolvedAt: DateInputFormik.validator().required(validationStrings.requiredField.errorMessage),
    severityId: Yup.number().required(validationStrings.requiredField.errorMessage),
});

interface MarkAsResolvedDialogProps {
    incidentId: string;
    isOpen: boolean;
    onClose: () => void;
}

export const MarkAsResolvedDialog = ({
    incidentId,
    isOpen,
    onClose,
}: MarkAsResolvedDialogProps) => {
    const [resolveIncident, { isLoading }] = useResolveIncidentMutation();
    const { data: severityLevels = [], isFetching: severityLevelsFetching } =
        useGetSeverityLevelsQuery(undefined, { skip: !isOpen });
    const [isSeverityLevelsModalOpen, setIsSeverityLevelsModalOpen] = useState(false);

    const severityLevelNames = severityLevels
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((s) => s.name);

    return (
        <>
            <Formik
                initialValues={{
                    resolvedAt: undefined as dayjs.Dayjs | undefined,
                    severityId: undefined as number | undefined,
                }}
                validationSchema={ValidationSchema}
                onSubmit={async (values, { resetForm }) => {
                    try {
                        const resolvedAtDate = dayjs(values.resolvedAt);
                        const resolvedAtIso = resolvedAtDate.isValid()
                            ? resolvedAtDate.toISOString()
                            : '';

                        await resolveIncident({
                            incidentId,
                            resolvedAt: resolvedAtIso,
                            severity: values.severityId!,
                        }).unwrap();

                        SuccessToast({ message: 'Incident marked as resolved.' });
                        resetForm();
                        onClose();
                    } catch (err) {
                        handleError('Unable to resolve incident. Please try again.', err as Error);
                    }
                }}
            >
                {(formik) => (
                    <DialogWithForm
                        dialogTitle="Mark as Resolved"
                        isOpen={isOpen}
                        secondaryButtonLabel="Cancel"
                        onSecondaryButtonClick={() => {
                            onClose();
                            formik.resetForm();
                        }}
                        primaryButtonLabel="Resolve Incident"
                        isPrimaryButtonDisabled={
                            !formik.isValid || formik.isSubmitting || isLoading
                        }
                        dialogContent={
                            <>
                                <DateInputFormik
                                    id="resolvedAt"
                                    label="Resolved At"
                                    showTime
                                    required
                                />
                                <SuggestSingleSelectFormik
                                    id="severityId"
                                    label="Severity"
                                    required
                                    disableAutoPopulate
                                    isLoading={severityLevelsFetching}
                                    values={severityLevelNames}
                                    selectedItem={
                                        severityLevels.find(
                                            (s) => s.id === formik.values.severityId,
                                        )?.name ?? undefined
                                    }
                                    onChange={(newValue) => {
                                        const level = severityLevels.find(
                                            (s) => s.name === newValue,
                                        );
                                        void formik.setFieldValue('severityId', level?.id);
                                    }}
                                />
                                <ActionButtonText
                                    label="View severity levels"
                                    onClick={() => setIsSeverityLevelsModalOpen(true)}
                                />
                            </>
                        }
                    />
                )}
            </Formik>
            <SeverityLevelsInfoModal
                isOpen={isSeverityLevelsModalOpen}
                onClose={() => setIsSeverityLevelsModalOpen(false)}
            />
        </>
    );
};
